/**
 * File type utility functions for checking MIME types and file extensions.
 * Centralizes all file type detection logic to avoid duplication across the codebase.
 */

/**
 * Image file extensions (lowercase)
 */
const IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.bmp',
  '.webp',
  '.svg'
];

/**
 * Checks if the content type (MIME type) represents an image.
 * @param contentType - The MIME type string (e.g., 'image/png')
 */
export function isImageMimeType(contentType: string): boolean {
  return contentType.startsWith('image/');
}

/**
 * Checks if the file name has an image extension.
 * @param fileName - The file name to check
 */
export function isImageExtension(fileName: string): boolean {
  const lowerCaseName = fileName.toLowerCase();

  return IMAGE_EXTENSIONS.some((ext) => lowerCaseName.endsWith(ext));
}

/**
 * Checks if the content type or file name indicates a PDF.
 * @param contentType - The MIME type string
 * @param fileName - Optional file name to check extension as fallback
 */
export function isPdf(contentType: string, fileName?: string): boolean {
  if (contentType === 'application/pdf') {
    return true;
  }
  if (fileName) {
    return fileName.toLowerCase().endsWith('.pdf');
  }

  return false;
}

/**
 * Gets the file extension from a file name (uppercase).
 * @param fileName - The file name to extract extension from
 * @returns The uppercase extension (e.g., 'PDF', 'DOCX') or 'FILE' if no extension
 */
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');

  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
}

/**
 * File extensions that we accept for upload but cannot be processed by the LLM
 * during RFQ creation (e.g. proprietary binary formats). The file is still
 * attached to the RFQ but won't be used to extract specs / build the RFQ.
 */
const UNPROCESSABLE_EXTENSIONS = ['.ai'];

/**
 * Returns true if the file's extension is allowed for upload but cannot be
 * read by the LLM and therefore won't contribute to RFQ generation.
 */
export function isUnprocessableForRfqExtraction(fileName: string): boolean {
  const lowerCaseName = fileName.toLowerCase();

  return UNPROCESSABLE_EXTENSIONS.some((ext) => lowerCaseName.endsWith(ext));
}

/** User-facing warning shown for files that pass through but can't be processed. */
export const UNPROCESSABLE_FILE_WARNING =
  'This file will be attached to the RFQ but won’t be used for RFQ creation.';
